import React from 'react'

const emptySlab = () => ({ from_search: '', to_search: '', credit_per_search: '' })

/**
 * Staggered (slab based) credit editor.
 *
 * Renders a list of credit slabs with three inputs per row:
 *  - From (searches)
 *  - To (searches)
 *  - Credits per search
 *
 * The component is controlled: the parent owns `slabs` and updates it through
 * `setSlabs`. If no slabs are provided it renders a single empty row.
 *
 * Props:
 *  - slabs:    Array<{ from_search, to_search, credit_per_search }>
 *  - setSlabs: (nextSlabs) => void
 */
const StaggerredCredit = ({ slabs, setSlabs }) => {
  const rows = Array.isArray(slabs) && slabs.length ? slabs : [emptySlab()]

  const updateRow = (index, field, value) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    setSlabs(next)
  }

  const addRow = () => {
    setSlabs([...rows, emptySlab()])
  }

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index)
    setSlabs(next.length ? next : [emptySlab()])
  }

  // Shared grid template so the header labels line up with the inputs.
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '28px 1fr 1fr 1fr 32px',
    columnGap: '16px',
    alignItems: 'center',
  }

  return (
    <div className="w-100">
      {/* Header */}
      <div style={{ ...gridStyle, marginBottom: '8px' }} className="text-gray-600">
        <span></span>
        <span style={{ fontWeight: 500 }}>From (searches)</span>
        <span style={{ fontWeight: 500 }}>To (searches)</span>
        <span style={{ fontWeight: 500 }}>Credits per search</span>
        <span></span>
      </div>

      {/* Rows */}
      {rows.map((row, index) => (
        <div key={index} style={{ ...gridStyle, marginBottom: '12px' }}>
          <span className="text-gray-600" style={{ textAlign: 'center' }}>{index + 1}</span>

          <input
            type="number"
            min={0}
            className="input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={row.from_search}
            onChange={(e) => updateRow(index, 'from_search', e.target.value)}
          />

          <input
            type="number"
            min={1}
            className="input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={row.to_search}
            onChange={(e) => updateRow(index, 'to_search', e.target.value)}
          />

          <input
            type="number"
            min={1}
            className="input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            value={row.credit_per_search}
            onChange={(e) => updateRow(index, 'credit_per_search', e.target.value)}
          />

          <button
            type="button"
            aria-label={`Remove slab ${index + 1}`}
            title="Remove slab"
            onClick={() => removeRow(index)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '25px',
              lineHeight: 1,
              color: '#6b7280',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>
      ))}

      <div style={{ marginTop: '8px' }}>
        <button type="button" className="btn btn-primary" onClick={addRow}>
          + Add slab
        </button>
      </div>
    </div>
  )
}

export default StaggerredCredit
